/**
 * MandantCaseHome — Landing-Seite des Mandanten nach Login + Redeem.
 *
 * Pendant zum Berater-CaseHub, aber stark vereinfacht:
 *   - keine Score-Karte (Score-Information ist Berater-Sicht)
 *   - keine Cockpit-Verlinkung
 *   - keine „Mandat löschen"-Funktionen
 *
 * Polish gegenüber MVP (Phase A):
 *   - Onboarding-Tour beim ersten Besuch (4 Karten, localStorage-Persist)
 *   - Vertrauensanker im Header: Petschwork-Logo + AVGS-§-45-Hinweis
 *   - Re-Login-friendly Pause-Hinweis
 *   - „Übersicht & Bestätigung"-CTA ab 80% Modulen
 *   - Mobile-Polish: 44px Touch-Targets, vertikaler Stapel auf <md
 *
 * Phase B (BWA-Konsolidierung):
 *   - Die alte separate „Anhang"-Sektion mit `<MandantBwaSection>` ist
 *     entfernt. Stattdessen ist die BWA-Box vollständig Teil der
 *     Multi-Dokument-Liste (`<MandantDocumentList>`), und ein Upload landet
 *     parallel in `case_documents` UND `financial_dataset.raw_bwa_storage_path`
 *     (siehe `MandantDocumentBox` + `linkBwaToFinancialDataset`).
 *   - Der „Ich habe keine BWA — manuell eintragen"-Pfad ist als Disclosure
 *     direkt unter der BWA-Box gerendert (`ManualBwaDisclosure` in
 *     `MandantDocumentList`).
 *
 * Wir nutzen denselben Wizard-Store (zustand-persist) wie der Berater. Das
 * funktioniert, weil der Store pro localStorage-Profil arbeitet — der Mandant
 * sitzt in seinem eigenen Browser-Profil. Die DB-Synchronisierung läuft über
 * useWizardSync (Background-Save), aber nur wenn der User echt eingeloggt ist.
 *
 * RLS-Schutz:
 *   - Diese Route ist nur erreichbar mit aktivem case_mandant_access (Guard
 *     in App.tsx). Wenn der Mandant per URL-Manipulation versucht, einen
 *     anderen Case zu öffnen, schlägt der Guard sowie getCase() fehl.
 *   - getCase() liefert null wenn RLS den Zugriff verweigert (statt 401) —
 *     wir mappen das auf eine freundliche „Kein Zugang"-Anzeige.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IdCard, ShieldCheck, Sparkles } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { getCase } from '../lib/supabaseCases';
import { selectStepStatus, useWizardStore } from '../lib/store/wizard';
import { STEPS, TOTAL_STEPS } from '../components/hub/dimensions';
import { SegmentedProgress } from '../components/hub/SegmentedProgress';
import { MandantDocumentList } from '../components/mandant/MandantDocumentList';
import { MandantClarificationsInbox } from '../components/mandant/MandantClarificationsInbox';
import {
  OnboardingTour,
  shouldShowOnboardingByDefault,
} from '../components/mandant/OnboardingTour';
import { caseToDraft, validateStammdaten } from '../lib/stammdaten/stammdatenValidation';
import { LoadingRows } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import type { CaseRow } from '../lib/supabaseCases';

interface CaseHeadline {
  readonly id: string;
  readonly clientFullName: string | null;
}

type StammdatenStatus = 'empty' | 'in-progress' | 'complete';

type LoadState =
  | { kind: 'checking' }
  | {
      kind: 'ready';
      caseInfo: CaseHeadline;
      stammdatenStatus: StammdatenStatus;
    }
  | { kind: 'not-found' }
  | { kind: 'error' };

const HANDOVER_STORAGE_PREFIX = 'mandant.handover.completed.';

export function MandantCaseHome() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { isMock } = useAuthOrSession();
  const ensureCase = useWizardStore((s) => s.ensureCase);
  const caseState = useWizardStore((s) => s.cases[caseId]);
  const [load, setLoad] = useState<LoadState>({ kind: 'checking' });
  const [reloadKey, setReloadKey] = useState(0);

  // Onboarding-Tour: zeige beim ersten Besuch automatisch, sonst nur auf
  // expliziten Re-Open-Klick im Footer.
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (load.kind === 'ready' && shouldShowOnboardingByDefault()) {
      setTourOpen(true);
    }
  }, [load.kind]);

  // Im Mock-Modus überspringen wir den DB-Roundtrip — sonst würde getCase auf
  // einen UUID-Check fallen und null liefern (nichts gibt's lokal). Wir bauen
  // einen Demo-Case-Stub für die UI-Entwicklung.
  useEffect(() => {
    let cancelled = false;
    if (isMock) {
      setLoad({
        kind: 'ready',
        caseInfo: { id: caseId, clientFullName: 'Demo-Mandant (Mock)' },
        stammdatenStatus: 'empty',
      });
      return;
    }

    setLoad({ kind: 'checking' });
    void (async () => {
      try {
        const c = await getCase(caseId);
        if (cancelled) return;
        if (!c) {
          // getCase liefert null nur bei echtem Nicht-Vorhanden / RLS-Deny —
          // das ist der legitime „kein Zugang"-Fall.
          setLoad({ kind: 'not-found' });
          return;
        }
        // Phase B: kein separater BWA-Pfad-Lookup mehr nötig — die BWA-Box
        // in MandantDocumentList lädt ihren eigenen State aus case_documents.
        if (cancelled) return;
        setLoad({
          kind: 'ready',
          caseInfo: { id: c.id, clientFullName: c.client_full_name },
          stammdatenStatus: deriveStammdatenStatus(c),
        });
      } catch (err) {
        // Ein geworfener Fehler ist ein transienter Fehler (Netzwerk, Timeout),
        // KEIN entzogener Zugang — sonst erschreckt ein kurzer Verbindungsabbruch
        // den Mandanten mit „Ihr Zugang wurde entzogen".
        console.warn('MandantCaseHome: getCase failed', err);
        if (!cancelled) setLoad({ kind: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caseId, isMock, reloadKey]);

  useEffect(() => {
    if (load.kind === 'ready') ensureCase(caseId);
  }, [load, caseId, ensureCase]);

  if (load.kind === 'checking') {
    return (
      <div className="pb-24 pt-12 md:pt-20">
        <LoadingRows rows={3} label="Mandat wird geladen" />
      </div>
    );
  }

  if (load.kind === 'not-found') {
    return (
      <div className="mx-auto max-w-prose pb-24 pt-12 md:pt-20">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandant.home.sectionLabel}
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
          {de.mandant.home.notFoundTitle}
        </h1>
        <p className="mt-4 font-serif text-base italic text-stone-500">
          {de.mandant.home.notFoundBody}
        </p>
        <Button
          type="button"
          onClick={() => {
            void navigate('/login');
          }}
          className="mt-8"
        >
          Zum Login →
        </Button>
      </div>
    );
  }

  if (load.kind === 'error') {
    return (
      <div className="mx-auto max-w-prose pb-24 pt-12 md:pt-20">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandant.home.sectionLabel}
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
          {de.mandant.home.loadErrorTitle}
        </h1>
        <p className="mt-4 font-serif text-base italic text-stone-500">
          {de.mandant.home.loadErrorBody}
        </p>
        <Button
          type="button"
          onClick={() => {
            setReloadKey((k) => k + 1);
          }}
          className="mt-8"
        >
          {de.mandant.home.loadErrorRetry} →
        </Button>
      </div>
    );
  }

  const completed = countCompleted(caseState);
  // Übersicht-CTA wird ab 80% verfügbar (oder wenn Mandant bereits einmal
  // übergeben hat — dann darf er wieder hin, um zu aktualisieren)
  const hasHandedOver = readHandover(caseId) !== null;
  const overviewAvailable = completed / TOTAL_STEPS >= 0.8 || hasHandedOver;

  return (
    <div className="pb-24 pt-6 md:pt-12">
      {tourOpen && (
        <OnboardingTour
          caseId={caseId}
          onClose={() => {
            setTourOpen(false);
          }}
        />
      )}

      <Hero completed={completed} />

      {/* Pause-Hinweis — re-login-friendly */}
      <p className="mt-6 flex items-start gap-2 rounded-sm bg-stone-50 px-4 py-3 font-serif text-sm italic leading-relaxed text-stone-600 md:mt-8">
        <Sparkles size={14} className="mt-1 flex-shrink-0 text-stone-500" aria-hidden="true" />
        <span>{de.mandant.home.pauseHint}</span>
      </p>

      {/* Modul-Liste */}
      <section
        className="mt-10 border-t border-stone-200"
        aria-label={de.mandant.home.modulesHeading}
      >
        <p className="mt-6 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandant.home.modulesHeading}
        </p>
        <ul className="mt-4 divide-y divide-stone-200 border-y border-stone-200">
          {/* Modul 00 — Stammdaten (vor Triage) */}
          <li>
            <button
              type="button"
              onClick={() => {
                void navigate(`/mandant/case/${caseId}/wizard/stammdaten`);
              }}
              className="group flex w-full min-h-[88px] flex-col items-stretch gap-3 py-4 text-left transition-colors hover:bg-stone-50 md:min-h-[unset] md:flex-row md:items-start md:justify-between md:gap-6 md:py-5"
            >
              <div className="flex items-start gap-4 min-w-0 flex-1">
                <span
                  aria-hidden="true"
                  className="mt-1 inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-sm bg-stone-200"
                >
                  <IdCard size={20} className="text-stone-700" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
                    Modul 00
                  </p>
                  <p className="mt-0.5 font-serif text-base font-bold text-stone-900">
                    {de.mandant.stammdaten.moduleLabel}
                  </p>
                  <p className="mt-1 font-serif text-sm italic text-stone-500">
                    {de.mandant.stammdaten.moduleSubtitle}
                  </p>
                </div>
              </div>
              <div className="flex flex-row-reverse items-center justify-between gap-3 md:flex-shrink-0 md:flex-col md:items-end md:self-center md:justify-center">
                <StammdatenStatusBadge status={load.stammdatenStatus} />
                <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-900 group-hover:underline">
                  {load.stammdatenStatus === 'complete' ? 'Ansehen →' : 'Bearbeiten →'}
                </span>
              </div>
            </button>
          </li>
          {STEPS.map((step) => {
            const status = caseState ? selectStepStatus(caseState, step.id) : 'untouched';
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => {
                    void navigate(`/mandant/case/${caseId}/wizard/${step.id}`);
                  }}
                  className="group flex w-full min-h-[88px] flex-col items-stretch gap-3 py-4 text-left transition-colors hover:bg-stone-50 md:min-h-[unset] md:flex-row md:items-start md:justify-between md:gap-6 md:py-5"
                >
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <span
                      aria-hidden="true"
                      className="mt-1 inline-block h-12 w-12 flex-shrink-0 rounded-sm"
                      style={{ backgroundColor: step.accentHex, opacity: 0.4 }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
                        Modul {step.nummer}
                      </p>
                      <p className="mt-0.5 font-serif text-base font-bold text-stone-900">
                        {step.name}
                      </p>
                      <p className="mt-1 font-serif text-sm italic text-stone-500">
                        {step.subtitle}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row-reverse items-center justify-between gap-3 md:flex-shrink-0 md:flex-col md:items-end md:self-center md:justify-center">
                    <StatusBadge status={status} />
                    <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-900 group-hover:underline">
                      {status === 'complete' ? 'Ansehen →' : 'Bearbeiten →'}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Rückfragen-Channel — Inbox + eigene offene Fragen */}
      <MandantClarificationsInbox caseId={caseId} />

      {/* Phase B: einzige BWA-Upload-Stelle. Die Box bedient parallel
          case_documents (Beweisspur) UND financial_dataset.raw_bwa_storage_path
          (Score-Engine), und enthält den manuellen BWA-Eingabe-Pfad als
          Disclosure direkt darunter. */}
      <MandantDocumentList caseId={caseId} />

      {/* Übersicht & Bestätigung — sichtbar ab 80% Modulen oder wenn schon übergeben */}
      <section className="mt-10 border-t border-stone-200 pt-8">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.mandant.home.overviewCtaLabel}
        </p>
        <div className="mt-3 flex flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-between">
          <p className="max-w-prose font-serif text-sm italic text-stone-500 md:text-base">
            {overviewAvailable
              ? 'Sie können Ihre Eingaben jetzt prüfen und an Ihren Berater übergeben.'
              : de.mandant.home.overviewCtaHint}
          </p>
          <Button
            type="button"
            onClick={() => {
              void navigate(`/mandant/case/${caseId}/uebersicht`);
            }}
            disabled={!overviewAvailable}
          >
            {de.mandant.home.overviewCtaButton}
          </Button>
        </div>
      </section>

      <p className="mt-12 max-w-prose border-t border-stone-200 pt-6 font-serif text-sm italic text-stone-500">
        {de.mandant.home.footerHint}
      </p>

      {/* Re-Open-Link für die Tour + Datenschutz-Verweise */}
      <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          onClick={() => {
            setTourOpen(true);
          }}
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline self-start"
        >
          {de.mandant.home.restartTour}
        </button>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <Link
            to={`/mandant/case/${caseId}/datenschutz`}
            className="inline-flex items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline"
          >
            <ShieldCheck size={12} aria-hidden="true" />
            Meine DSGVO-Rechte
          </Link>
          <Link
            to="/datenschutz"
            className="inline-flex items-center gap-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline"
          >
            <ShieldCheck size={12} aria-hidden="true" />
            {de.footer.privacyLink}
          </Link>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Hero — mit Vertrauensanker (Petschwork-Logo + AVGS-Hinweis)
// -----------------------------------------------------------------------------

function Hero({ completed }: { completed: number }) {
  const [line1, line2] = de.mandant.home.title.split('\n');
  return (
    <header className="relative pb-6 pt-2 md:pb-10 md:pt-6">
      <div
        aria-hidden="true"
        className="absolute right-0 top-6 hidden h-24 w-24 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-private)', opacity: 0.18 }}
      />
      <div
        aria-hidden="true"
        className="absolute right-16 top-16 hidden h-12 w-12 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-identity)', opacity: 0.32 }}
      />

      {/* Vertrauensanker: Logo + AVGS-Hinweis */}
      <div className="flex items-center gap-3">
        <img
          src="/petschwork-logo.png"
          alt="Petschwork Consulting"
          className="h-10 w-auto md:h-12"
          loading="eager"
          decoding="async"
        />
        <div className="min-w-0">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.mandant.home.sectionLabel}
          </p>
          <p className="mt-0.5 font-serif text-xs italic text-stone-500">
            {de.mandant.home.headerSubtitle}
          </p>
        </div>
      </div>

      <h1 className="mt-5 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:mt-7 md:text-6xl">
        {line1}
        {line2 && (
          <>
            <br />
            {line2}
          </>
        )}
      </h1>
      <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:mt-4 md:text-lg">
        {de.mandant.home.subtitle}
      </p>

      <div className="mt-6 max-w-md md:mt-8">
        <SegmentedProgress total={STEPS.length} completed={completed} />
      </div>
    </header>
  );
}

function StammdatenStatusBadge({ status }: { status: StammdatenStatus }) {
  const cls =
    status === 'complete'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'in-progress'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-stone-100 text-stone-500';
  const label =
    status === 'complete'
      ? de.mandantBoard.stammdatenComplete
      : status === 'in-progress'
        ? de.mandantBoard.stammdatenIncomplete
        : de.mandantBoard.stammdatenEmpty;
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${cls}`}
    >
      {label}
    </span>
  );
}

/**
 * Bestimmt aus dem CaseRow den Stammdaten-Status.
 *
 * 'complete' wenn validateStammdaten().isComplete = true
 * 'in-progress' wenn mind. ein Pflichtfeld gesetzt ist, aber nicht alle
 * 'empty' wenn gar keine Stammdaten gesetzt sind
 */
function deriveStammdatenStatus(c: CaseRow): StammdatenStatus {
  const draft = caseToDraft(c);
  const validation = validateStammdaten(draft);
  if (validation.isComplete) return 'complete';
  const anyValue =
    (c.client_full_name ?? '').length > 0 ||
    (c.client_birthdate ?? '').length > 0 ||
    (c.client_address ?? '').length > 0 ||
    (c.business_branch ?? '').length > 0 ||
    (c.business_legal_form ?? '').length > 0 ||
    (c.business_start_date ?? '').length > 0 ||
    (c.business_description ?? '').length > 0;
  return anyValue ? 'in-progress' : 'empty';
}

function StatusBadge({ status }: { status: 'untouched' | 'in-progress' | 'complete' }) {
  const cls =
    status === 'complete'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'in-progress'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-stone-100 text-stone-500';
  const label =
    status === 'complete'
      ? de.mandantBoard.statusComplete
      : status === 'in-progress'
        ? de.mandantBoard.statusInProgress
        : de.mandantBoard.statusEmpty;
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${cls}`}
    >
      {label}
    </span>
  );
}

function countCompleted(
  c: ReturnType<typeof useWizardStore.getState>['cases'][string] | undefined,
): number {
  if (!c) return 0;
  let n = 0;
  for (const step of STEPS) {
    if (selectStepStatus(c, step.id) === 'complete') n += 1;
  }
  return n;
}

/** Liest den Übergabe-Marker für die Übersicht-CTA-Verfügbarkeit. */
function readHandover(caseId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(HANDOVER_STORAGE_PREFIX + caseId);
  } catch {
    return null;
  }
}
